import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/api.js';
import { useLang } from '../lib/i18n.js';
import { PageHead, Badge, Switch, EmptyState } from '../components/ui.js';
import { Icon, BrandGlyph } from '../components/Icon.js';

const META_APP_ID = import.meta.env.VITE_META_APP_ID as string | undefined;
const META_API_VERSION = (import.meta.env.VITE_META_API_VERSION as string | undefined) ?? 'v25.0';
const META_LOGIN_CONFIG_ID = import.meta.env.VITE_META_LOGIN_CONFIG_ID as string | undefined;
const SCOPES = [
  'pages_show_list', 'pages_messaging', 'pages_manage_metadata', 'pages_manage_engagement',
  'instagram_basic', 'instagram_manage_messages', 'instagram_manage_comments', 'business_management',
].join(',');

interface MetaConnection {
  meta_connection_id: string;
  meta_user_id: string;
  page_id: string;
  page_name: string;
  ig_business_account_id: string | null;
  ig_username: string | null;
  comments_auto_reply_enabled: boolean;
  status: string;
  token_expires_at: string | null;
  connected_at: string;
}

export function ConnectMeta() {
  const api = useApi();
  const qc = useQueryClient();
  const { t, lang } = useLang();
  const [sdkReady, setSdkReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!META_APP_ID) return;
    loadFbSdk(META_APP_ID, META_API_VERSION)
      .then(() => setSdkReady(true))
      .catch((e) => setError((e as Error).message));
  }, []);

  const { data } = useQuery({
    queryKey: ['meta-connections'],
    queryFn: () => api.get<{ connections: MetaConnection[]; total: number }>('/meta-connections'),
  });

  const exchange = useMutation({
    mutationFn: (short_lived_user_token: string) => api.post('/meta-connections/exchange', { short_lived_user_token }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meta-connections'] }),
  });
  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.patch(`/meta-connections/${id}`, { comments_auto_reply_enabled: enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meta-connections'] }),
  });
  const disconnect = useMutation({
    mutationFn: (id: string) => api.delete(`/meta-connections/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meta-connections'] }),
  });

  function onConnectClick() {
    setError(null);
    if (!window.FB) { setError(t('page.channels.err.sdk')); return; }
    const loginOptions = META_LOGIN_CONFIG_ID
      ? { config_id: META_LOGIN_CONFIG_ID, response_type: 'token' as const }
      : { scope: SCOPES, auth_type: 'rerequest' as const, return_scopes: true };
    window.FB.login(
      (response) => {
        if (response.status === 'connected' && response.authResponse?.accessToken) {
          exchange.mutate(response.authResponse.accessToken);
        } else if (response.status === 'not_authorized') {
          setError(t('page.channels.err.declined'));
        } else {
          setError(t('page.channels.err.incomplete'));
        }
      },
      loginOptions,
    );
  }

  const connections = data?.connections ?? [];
  const dateLocale = lang === 'ar' ? 'ar-EG' : 'en-US';

  return (
    <div className="wrap section-gap">
      <PageHead
        title={t('page.channels.title')}
        sub={t('page.channels.sub')}
        actions={
          <button
            className="btn btn--primary"
            onClick={onConnectClick}
            disabled={!sdkReady || !META_APP_ID}
            style={{ background: '#1877F2', color: '#fff' }}
          >
            <BrandGlyph name="fb" size={16} />
            {!META_APP_ID ? t('page.channels.notSet') : sdkReady ? t('page.channels.connect') : t('page.channels.loading')}
          </button>
        }
      />

      {error && (
        <div className="card card--pad" style={{ borderColor: 'var(--danger-soft)', background: 'var(--danger-soft)' }}>
          <div className="flex aic gap8" style={{ color: 'var(--danger)', fontSize: 13.5, fontWeight: 600 }}>
            <Icon name="shield" size={16} />{error}
          </div>
        </div>
      )}

      {exchange.isPending && (
        <div className="card card--pad" style={{ borderColor: 'var(--accent-line)' }}>
          <div className="flex aic gap8" style={{ color: 'var(--accent)', fontSize: 13.5, fontWeight: 600 }}>
            <Icon name="refresh" size={16} />{t('page.channels.saving')}
          </div>
        </div>
      )}

      {connections.length === 0 ? (
        <EmptyState icon="plug" h={t('page.channels.empty.h')} p={t('page.channels.empty.p')} />
      ) : (
        <div className="grid2">
          {connections.map((conn) => (
            <div className="card card--pad" key={conn.meta_connection_id}>
              <div className="between" style={{ alignItems: 'flex-start' }}>
                <div className="flex aic gap12" style={{ minWidth: 0 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 12,
                    background: 'linear-gradient(135deg, #833ab4, #fd1d1d, #fcb045)',
                    display: 'grid', placeItems: 'center', flexShrink: 0,
                  }}>
                    <BrandGlyph name="ig" size={20} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conn.page_name}</div>
                    {conn.ig_username && <div className="muted mono" style={{ fontSize: 12.5, marginTop: 2 }} dir="ltr">@{conn.ig_username}</div>}
                  </div>
                </div>
                {conn.status === 'active'
                  ? <Badge kind="ok" plain icon="checkcircle">{t('status.active')}</Badge>
                  : <Badge kind="muted" plain>{conn.status}</Badge>}
              </div>

              <div className="flex gap8 wrapf" style={{ marginTop: 14 }}>
                <Badge kind="muted" plain icon="external"><span className="mono" dir="ltr">FB {conn.page_id.slice(-10)}</span></Badge>
                {conn.token_expires_at && (
                  <Badge kind="warn" plain icon="clock">
                    {t('page.channels.tokenExpires')} {new Date(conn.token_expires_at).toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', year: 'numeric' })}
                  </Badge>
                )}
              </div>

              <div className="between" style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--line)' }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700 }}>{t('page.channels.autoReply')}</div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>{t('page.channels.autoReplyHint')}</div>
                </div>
                <Switch
                  on={conn.comments_auto_reply_enabled}
                  onClick={() => toggle.mutate({ id: conn.meta_connection_id, enabled: !conn.comments_auto_reply_enabled })}
                />
              </div>

              <button
                className="btn btn--danger btn--sm"
                onClick={() => { if (confirm(t('page.channels.disConfirm'))) disconnect.mutate(conn.meta_connection_id); }}
                style={{ marginTop: 14 }}
              >
                <Icon name="trash" />{t('page.channels.disconnect')}
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="card card--pad">
        <div className="flex aic gap8" style={{ marginBottom: 10 }}>
          <Icon name="shield" />
          <h3 style={{ fontSize: 14.5, fontWeight: 800, margin: 0 }}>{t('page.channels.req.h')}</h3>
        </div>
        <ul style={{ listStyle: 'disc', paddingInlineStart: 22, color: 'var(--ink-2)', fontSize: 13.5, lineHeight: 1.85, margin: 0 }}>
          <li>{t('page.channels.req.1')}</li>
          <li>{t('page.channels.req.2')}</li>
          <li>{t('page.channels.req.3')}</li>
        </ul>
      </div>
    </div>
  );
}

declare global {
  interface Window {
    FB: {
      init: (opts: { appId: string; version: string; cookie?: boolean; xfbml?: boolean }) => void;
      login: (
        cb: (resp: { status: string; authResponse?: { accessToken: string; userID: string; grantedScopes?: string } }) => void,
        opts:
          | { scope: string; auth_type?: string; return_scopes?: boolean }
          | { config_id: string; response_type?: 'token' | 'code' },
      ) => void;
    };
    fbAsyncInit?: () => void;
  }
}

function loadFbSdk(appId: string, version: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.FB) { resolve(); return; }
    window.fbAsyncInit = () => {
      window.FB.init({ appId, version, cookie: true, xfbml: false });
      resolve();
    };
    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.onerror = () => reject(new Error('Failed to load Facebook SDK'));
    document.body.appendChild(script);
  });
}
